import { useState, useEffect } from "react";
import ApiService from "../services/ApiService";
import UserAuthService from "../services/UserAuthService";

export type TrialModeState = {
    isTrialMode: boolean;
    loading: boolean;
    error: string | null;
};

/**
 * Custom hook to manage trial mode state across the application.
 *
 * This hook:
 * - Fetches trial mode status from the backend when user logs in
 * - Automatically updates when auth state changes
 * - Provides loading and error states for UI feedback
 *
 * We use a hook instead of storing in UserAuthService to keep auth service
 * stateless as noted in CLAUDE.md technical notes.
 */
export const useTrialMode = (apiService: ApiService, authService: UserAuthService): TrialModeState => {
    const [state, setState] = useState<TrialModeState>({
        isTrialMode: false,
        loading: false,
        error: null,
    });

    // Fetch trial mode status from backend
    const fetchTrialMode = async () => {
        // Don't fetch if user isn't logged in
        if (!authService.isLoggedIn()) {
            setState({ isTrialMode: false, loading: false, error: null });
            return;
        }

        setState((prev) => ({ ...prev, loading: true, error: null }));

        try {
            // Use existing /auth/verify endpoint which already includes isTrialMode
            const response = await apiService.verifyAuthentication();
            setState({
                isTrialMode: response.isTrialMode,
                loading: false,
                error: null,
            });
        } catch (error) {
            setState((prev) => ({
                ...prev,
                loading: false,
                error: error instanceof Error ? error.message : "Failed to check trial mode",
            }));
        }
    };

    useEffect(() => {
        // Listen for auth changes and update trial mode accordingly
        const unsubscribe = authService.onAuthStateChanged((user) => {
            if (user) {
                fetchTrialMode();
            } else {
                // Clear trial mode when user logs out
                setState({ isTrialMode: false, loading: false, error: null });
            }
        });

        // Initial fetch if user is already authenticated
        if (authService.isLoggedIn() && authService.isInitialized()) {
            fetchTrialMode();
        }

        return unsubscribe;
    }, [apiService, authService]);

    return state;
};
